/**
 * card-hover.js
 * Handles improved hover behavior for cards to prevent "hover trapping"
 * This solves the issue where enlarged cards capture mouse events and make it difficult
 * to hover over adjacent cards.
 */

import { CardDrag } from './card-drag.js';
import { CardDescEngraving } from './card-desc-engraving.js';
import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { TouchPreview } from './touch-preview.js';
import { GameState } from '../game-state.js';
let CardHover = {};

document.addEventListener('DOMContentLoaded', () => {
    // Wait a short moment to ensure all other scripts are initialized
    setTimeout(() => {
        initializeCardHoverBehavior();
    }, 500);
});

const CARD_REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedCardMotion() {
    return window.matchMedia?.(CARD_REDUCED_MOTION_QUERY).matches === true;
}

function isWithinRetentionArea(pointer, pos, options = {}) {
    if (!pointer || !pos) return false;
    const x = Number(pointer.x);
    const y = Number(pointer.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;

    const width = Number(pos.width) || Math.max(0, (Number(pos.right) || 0) - (Number(pos.left) || 0));
    const height = Number(pos.height) || Math.max(0, (Number(pos.bottom) || 0) - (Number(pos.top) || 0));
    if (!width || !height) return false;

    const sidePadding = Number.isFinite(options.sidePadding)
        ? options.sidePadding
        : Math.max(width * 1.35, 120);
    const bottomPadding = Number.isFinite(options.bottomPadding)
        ? options.bottomPadding
        : 12;
    const topExpansion = Number.isFinite(options.topExpansion)
        ? options.topExpansion
        : Math.max(height * 2.1, 280);

    const left = (Number(pos.left) || 0) - sidePadding;
    const right = (Number(pos.right) || 0) + sidePadding;
    const top = (Number(pos.top) || 0) - topExpansion;
    const bottom = (Number(pos.bottom) || 0) + bottomPadding;

    return x >= left && x <= right && y >= top && y <= bottom;
}

function isWithinCardVisualBounds(pointer, card, options = {}) {
    if (!pointer || !card || typeof card.getBoundingClientRect !== 'function') return false;
    const x = Number(pointer.x);
    const y = Number(pointer.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;

    const rect = card.getBoundingClientRect();
    const width = Number(rect.width) || 0;
    const height = Number(rect.height) || 0;
    if (!width || !height) return false;

    const sidePadding = Number.isFinite(options.sidePadding)
        ? options.sidePadding
        : 10;
    const topPadding = Number.isFinite(options.topPadding)
        ? options.topPadding
        : 10;
    const bottomPadding = Number.isFinite(options.bottomPadding)
        ? options.bottomPadding
        : 12;

    const left = rect.left - sidePadding;
    const right = rect.right + sidePadding;
    const top = rect.top - topPadding;
    const bottom = rect.bottom + bottomPadding;
    return x >= left && x <= right && y >= top && y <= bottom;
}

function isPointerOverElement(pointer, element) {
    if (!pointer || !element || typeof document === 'undefined' || typeof document.elementsFromPoint !== 'function') {
        return false;
    }
    const x = Number(pointer.x);
    const y = Number(pointer.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;

    const elements = document.elementsFromPoint(x, y);
    if (!Array.isArray(elements) || !elements.length) return false;

    return elements.some((candidate) => candidate === element || (
        typeof element.contains === 'function' && element.contains(candidate)
    ));
}

function hasPointerEventsDisabled(element) {
    if (!element || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
        return false;
    }
    try {
        return window.getComputedStyle(element).pointerEvents === 'none';
    } catch (_) {
        return false;
    }
}

function isWithinActiveCardHoverTarget(pointer, card, options = {}) {
    const constrainToRetentionArea = options.constrainToRetentionArea === true;
    const retentionPos = options.retentionPos || options.activePos;
    const retentionAreaOptions = options.retentionAreaOptions;

    if (constrainToRetentionArea) {
        return isWithinRetentionArea(pointer, retentionPos, retentionAreaOptions);
    }

    const supportsPreciseHitTest = typeof document !== 'undefined' && typeof document.elementsFromPoint === 'function';
    const pointerEventsDisabled = hasPointerEventsDisabled(card);
    const allowVisualBoundsFallback = pointerEventsDisabled ||
        options.useVisualBoundsFallback === true ||
        (!supportsPreciseHitTest && options.useVisualBoundsFallback !== false);

    const visualBoundsOptions = options.visualBoundsOptions || {
        sidePadding: 10,
        topPadding: 10,
        bottomPadding: 12
    };
    const pointerDisabledBoundsOptions = pointerEventsDisabled ? {
        sidePadding: Math.max(Number(visualBoundsOptions.sidePadding) || 0, 24),
        topPadding: Math.max(Number(visualBoundsOptions.topPadding) || 0, 56),
        bottomPadding: Math.max(Number(visualBoundsOptions.bottomPadding) || 0, 16)
    } : visualBoundsOptions;
    const withinVisualBounds = allowVisualBoundsFallback &&
        isWithinCardVisualBounds(pointer, card, pointerDisabledBoundsOptions);
    if (options.preferVisualBounds === true && allowVisualBoundsFallback) {
        return withinVisualBounds;
    }

    if (isPointerOverElement(pointer, card)) {
        return true;
    }

    if (withinVisualBounds) {
        return true;
    }

    if (supportsPreciseHitTest) {
        return false;
    }
    return false;
}

function shouldRetainActiveHover(pointer, card, activePos, options = {}) {
    const constrainElementHitToRetentionArea = options.constrainElementHitToRetentionArea === true;
    const retentionAreaOptions = options.retentionAreaOptions || {
        sidePadding: Number.isFinite(options.sidePadding) ? options.sidePadding : 24,
        bottomPadding: Number.isFinite(options.bottomPadding) ? options.bottomPadding : 10,
        topExpansion: Number.isFinite(options.topExpansion)
            ? options.topExpansion
            : Math.max((activePos?.height || 0) * 0.95, 140)
    };

    if (constrainElementHitToRetentionArea) {
        return isWithinRetentionArea(pointer, activePos, retentionAreaOptions);
    }

    const supportsPreciseHitTest = typeof document !== 'undefined' && typeof document.elementsFromPoint === 'function';
    const pointerEventsDisabled = hasPointerEventsDisabled(card);
    const allowVisualBoundsFallback = pointerEventsDisabled ||
        options.useVisualBoundsFallback === true ||
        (!supportsPreciseHitTest && options.useVisualBoundsFallback !== false);
    const visualBoundsOptions = options.visualBoundsOptions || {
        sidePadding: 10,
        topPadding: 10,
        bottomPadding: 12
    };
    const pointerDisabledBoundsOptions = pointerEventsDisabled ? {
        sidePadding: Math.max(Number(visualBoundsOptions.sidePadding) || 0, 24),
        topPadding: Math.max(Number(visualBoundsOptions.topPadding) || 0, 56),
        bottomPadding: Math.max(Number(visualBoundsOptions.bottomPadding) || 0, 16)
    } : visualBoundsOptions;
    const withinVisualBounds = allowVisualBoundsFallback &&
        isWithinCardVisualBounds(pointer, card, pointerDisabledBoundsOptions);

    if (options.preferVisualBounds === true && allowVisualBoundsFallback) {
        if (withinVisualBounds) {
            return true;
        }
    } else {
        if (isPointerOverElement(pointer, card)) {
            return true;
        }
        if (withinVisualBounds) {
            return true;
        }
    }

    const activatedAt = Number(options.activatedAt) || 0;
    const now = Number.isFinite(options.now) ? options.now : Date.now();
    const graceMs = Number.isFinite(options.graceMs) ? options.graceMs : 180;
    if (!(activatedAt > 0 && (now - activatedAt) < graceMs)) {
        return false;
    }

    return isWithinRetentionArea(pointer, activePos, retentionAreaOptions);
}

function isWithinSwitchTargetCore(pointer, pos, options = {}) {
    if (!pointer || !pos) return false;
    const x = Number(pointer.x);
    const y = Number(pointer.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;

    const width = Number(pos.width) || Math.max(0, (Number(pos.right) || 0) - (Number(pos.left) || 0));
    const height = Number(pos.height) || Math.max(0, (Number(pos.bottom) || 0) - (Number(pos.top) || 0));
    if (!width || !height) return false;

    const sideInset = Number.isFinite(options.sideInset)
        ? options.sideInset
        : Math.min(Math.max(width * 0.18, 12), 30);
    const topInset = Number.isFinite(options.topInset)
        ? options.topInset
        : Math.min(Math.max(height * 0.08, 8), 22);
    const bottomInset = Number.isFinite(options.bottomInset)
        ? options.bottomInset
        : Math.min(Math.max(height * 0.08, 8), 22);

    const left = (Number(pos.left) || 0) + sideInset;
    const right = (Number(pos.right) || 0) - sideInset;
    const top = (Number(pos.top) || 0) + topInset;
    const bottom = (Number(pos.bottom) || 0) - bottomInset;

    if (left >= right || top >= bottom) {
        return x >= (Number(pos.left) || 0) && x <= (Number(pos.right) || 0) &&
            y >= (Number(pos.top) || 0) && y <= (Number(pos.bottom) || 0);
    }

    return x >= left && x <= right && y >= top && y <= bottom;
}

function clamp(value, min, max) {
    if (!Number.isFinite(value)) return min;
    return Math.min(max, Math.max(min, value));
}

function resolveTiltMax(card, fallback = 10) {
    if (!card || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
        return fallback;
    }
    const parsed = parseFloat(window.getComputedStyle(card).getPropertyValue('--card-tilt-max'));
    return Number.isFinite(parsed) ? parsed : fallback;
}

function applyTiltFromPointer(card, pointer, options = {}) {
    if (!card || !pointer) return false;
    if (prefersReducedCardMotion()) {
        resetCardTilt(card);
        return false;
    }
    const rect = typeof card.getBoundingClientRect === 'function'
        ? card.getBoundingClientRect()
        : null;
    if (!rect) return false;

    const width = rect.width || card.offsetWidth || 0;
    const height = rect.height || card.offsetHeight || 0;
    if (!width || !height) return false;

    const edgePaddingX = Number.isFinite(options.edgePaddingX)
        ? Math.max(0, options.edgePaddingX)
        : 0;
    const edgePaddingY = Number.isFinite(options.edgePaddingY)
        ? Math.max(0, options.edgePaddingY)
        : 0;
    const normalizedWidth = width + edgePaddingX * 2;
    const normalizedHeight = height + edgePaddingY * 2;

    const x = clamp((Number(pointer.x) - (rect.left - edgePaddingX)) / normalizedWidth, 0, 1);
    const y = clamp((Number(pointer.y) - (rect.top - edgePaddingY)) / normalizedHeight, 0, 1);
    const maxTilt = Number.isFinite(options.maxTilt)
        ? options.maxTilt
        : resolveTiltMax(card, 10);

    const rotateX = (0.5 - y) * maxTilt * 2;
    const rotateY = (x - 0.5) * maxTilt * 2;

    card.classList.add('card-tilt-active');
    card.style.setProperty('--card-tilt-x', x.toFixed(3));
    card.style.setProperty('--card-tilt-y', y.toFixed(3));
    card.style.setProperty('--card-tilt-rotate-x', `${rotateX.toFixed(2)}deg`);
    card.style.setProperty('--card-tilt-rotate-y', `${rotateY.toFixed(2)}deg`);
    card.style.setProperty('--card-tilt-opacity', '1');
    return true;
}

function getHandHoverRetentionOptions(pos) {
    const width = Number(pos?.width) || 0;
    const height = Number(pos?.height) || 0;
    return {
        sidePadding: Math.max(Math.min(width * 0.9, 118), 60),
        topExpansion: Math.max(height * 1.85, 250),
        bottomPadding: 12
    };
}

function resetCardTilt(card) {
    if (!card) return;
    card.classList.remove('card-tilt-active');
    card.style.setProperty('--card-tilt-x', '0.5');
    card.style.setProperty('--card-tilt-y', '0.5');
    card.style.setProperty('--card-tilt-rotate-x', '0deg');
    card.style.setProperty('--card-tilt-rotate-y', '0deg');
    card.style.setProperty('--card-tilt-opacity', '0');
}

CardHover.isWithinRetentionArea = isWithinRetentionArea;
CardHover.isWithinCardVisualBounds = isWithinCardVisualBounds;
CardHover.isPointerOverElement = isPointerOverElement;
CardHover.isWithinActiveCardHoverTarget = isWithinActiveCardHoverTarget;
CardHover.shouldRetainActiveHover = shouldRetainActiveHover;
CardHover.isWithinSwitchTargetCore = isWithinSwitchTargetCore;
CardHover.applyTiltFromPointer = applyTiltFromPointer;
CardHover.getHandHoverRetentionOptions = getHandHoverRetentionOptions;
CardHover.resetCardTilt = resetCardTilt;

/**
 * Sets up improved hover behavior for cards in the player's hand
 */
function initializeCardHoverBehavior() {
    // Store original card positions and dimensions
    const cardPositions = new Map();
    let isUpdatingPositions = false;
    let queuedPositionRefreshDelay = null;
    let positionRefreshTimer = null;
    let positionRefreshDueAt = 0;
    let activeCard = null;
    let activeCardVisualRect = null;
    let pendingCard = null;
    let pointerRevision = 0;
    let dragInProgress = false; // ensure overlay never re-enables during drag
    let overlayLockedToNone = false; // keep overlay non-interactive for draggable cards
    let handleHandMouseMove = null;
    const HOVER_DRAG_DEBUG = window.CLIENT_DEBUG_MODE;
    const hoverLog = (...args) => {
        if (!HOVER_DRAG_DEBUG) return;
        console.log('[DRAG-HOVER]', ...args);
    };

    const setStellarHoverState = (card, isHovered) => {
        if (!card) return;
        if (Cards?.setStellarAudioHover) {
            Cards.setStellarAudioHover(card, isHovered);
        }
    };

    const resetHoverCard = (card) => {
        if (!card) return;
        setStellarHoverState(card, false);
        card.classList.remove('hover-active', 'safari-hand-zoom-hover');
        card.style.removeProperty('--card-hover-transform');
        card.style.zoom = '';
        // At rest again: the natural paragraph, byte-identical.
        CardDescEngraving.release(card);
        card.style.margin = '';
        card.style.transformOrigin = '';
        resetCardTilt(card);
        const hint = card.querySelector('.card-hint');
        if (hint) {
            hint.textContent = '';
            hint.classList.remove('visible-hint');
        }
        Cards?.hideSideHint?.(card);
    };

    const getHandTiltPointerOptions = (card) => ({
        edgePaddingX: Math.max((card?.offsetWidth || 0) * 0.5, 48),
        edgePaddingY: Math.max((card?.offsetHeight || 0) * 0.9, 96)
    });

    function applyDynamicHoverTransform(card) {
        if (!card) return;
        /* The zoom below re-lays-out the description at up to 3.8x, and a
           line on its wrap point re-wraps there. Engrave the resting lines
           first — only from rest; this runs again while the card is held —
           and keep them until resetHoverCard (card-desc-engraving.js). */
        if (!((parseFloat(card.style.zoom) || 1) > 1)) CardDescEngraving.engrave(card);
        if (Cards?.applyHoverTransform) {
            Cards.applyHoverTransform(card, {
                baseScale: 3.8,
                minScale: 1.6,
                margin: 16,
                origin: 'bottom center',
                translateYPercent: -15
            });
        }
    }

    function measureCardPosition(card) {
        if (!card?.isConnected) return null;
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        const position = {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height
        };
        cardPositions.set(card, position);
        return position;
    }

    function measureConnectedVisualRect(card) {
        if (!card?.isConnected || typeof card.getBoundingClientRect !== 'function') return null;
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        return {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height
        };
    }

    function paintedHandCardAtPoint(pointer) {
        if (typeof document.elementsFromPoint !== 'function') return null;
        const elements = document.elementsFromPoint(pointer.x, pointer.y);
        for (const element of elements || []) {
            const card = element?.closest?.('#player-hand .card');
            if (card && !card.classList.contains('dragging')) return card;
        }
        return null;
    }

    function readLastHandPointer() {
        return {
            x: Number(window.lastMouseX),
            y: Number(window.lastMouseY)
        };
    }

    const ACTIVE_HAND_STYLE_PROPERTIES = [
        '--card-hover-transform',
        '--card-tilt-x',
        '--card-tilt-y',
        '--card-tilt-rotate-x',
        '--card-tilt-rotate-y',
        '--card-tilt-opacity'
    ];

    function handoffActiveCardToReplacement() {
        const source = activeCard;
        const instanceId = source?.dataset?.instanceId;
        if (!source || source.isConnected || !instanceId || !activeCardVisualRect) return false;

        const replacement = Array.from(document.querySelectorAll('#player-hand .card'))
            .find((card) => card.dataset?.instanceId === instanceId);
        const pointer = readLastHandPointer();
        const scheduledPointerRevision = pointerRevision;
        const supportsPaintHitTest = typeof document.elementsFromPoint === 'function';
        const paintedReplacement = replacement && supportsPaintHitTest
            ? paintedHandCardAtPoint(pointer)
            : null;
        if (pointerRevision !== scheduledPointerRevision) return false;
        const retainsPreviousVisual = replacement && isWithinRetentionArea(
            pointer,
            activeCardVisualRect,
            { sidePadding: 0, topExpansion: 0, bottomPadding: 0 }
        );
        if (!replacement || (
            supportsPaintHitTest
            && paintedReplacement !== replacement
            && !retainsPreviousVisual
        )) return false;

        const sourcePosition = cardPositions.get(source);
        const sourceStyles = Object.fromEntries(ACTIVE_HAND_STYLE_PROPERTIES.map((property) => (
            [property, source.style.getPropertyValue(property)]
        )));
        const sourceZoom = source.style.zoom;
        const sourceMargin = source.style.margin;
        const sourceTransformOrigin = source.style.transformOrigin;
        const sourceUsesSafariZoom = source.classList.contains('safari-hand-zoom-hover');
        const activatedAt = source._hoverActivatedAt;
        // The held card's resting lines and title, read from its engraving
        // before the reset releases it (the source is detached: nothing to measure).
        const sourceDescription = {
            layout: CardDescEngraving.layoutOf(source),
            fontSize: source.querySelector?.('.card-desc')?.style.fontSize || '',
            title: CardDescEngraving.titleStateOf(source),
        };

        cancelCardHoverWork(source);
        resetHoverCard(source);
        cardPositions.delete(source);

        // CardTilt intentionally delegates hand hit-testing to this controller,
        // but it still owns the material node. Registering here is idempotent and
        // happens in the same mutation checkpoint as the authoritative rebuild,
        // before the browser can paint a rotation-only replacement frame.
        CardTilt?.register?.(replacement);
        /* THE HANDOFF IS NOT AN ENTRANCE, FOR THE CARD EITHER. The paint hit
           test above already forced a style pass that resolved the replacement
           at its RESTING fan pose, and `#player-hand .card` transitions
           `transform` and `box-shadow` over 150 ms. Taking the hover state with
           those live replayed the grow on every snapshot: the first frame after
           an enemy action showed the card rotated back to its fan angle without
           its lift, then it straightened and rose again (and on the transform
           path it collapsed to scale(1) and re-grew). 25 September 2026. The
           state lands with transitions off; measureConnectedVisualRect below
           commits it, and the stylesheet's transitions return after that frame
           so a later leave or enter still animates. */
        const replacementStyle = /** @type {HTMLElement} */ (replacement).style;
        const replacementTransition = replacementStyle.transition;
        replacementStyle.transition = 'none';
        window.requestAnimationFrame?.(() => {
            window.requestAnimationFrame?.(() => {
                if (replacementStyle.transition !== 'none') return;
                if (replacementTransition) {
                    replacementStyle.transition = replacementTransition;
                } else {
                    replacementStyle.removeProperty('transition');
                }
            });
        });
        const glare = replacement.querySelector(':scope > .card-glare');
        if (glare) {
            // This is a continuity handoff, not a fresh hover entrance. Suppress
            // the ordinary 200ms fade for the replacement's first paint, then
            // restore normal leave/enter transitions once that frame commits.
            glare.style.transition = 'none';
            window.requestAnimationFrame?.(() => {
                window.requestAnimationFrame?.(() => {
                    if (glare.isConnected && replacement.contains(glare)) {
                        glare.style.removeProperty('transition');
                    }
                });
            });
        }
        replacement.classList.add('hover-active', 'card-tilt-active');
        if (sourceUsesSafariZoom) replacement.classList.add('safari-hand-zoom-hover');
        ACTIVE_HAND_STYLE_PROPERTIES.forEach((property) => {
            const value = sourceStyles[property];
            if (value) replacement.style.setProperty(property, value);
        });
        // The replacement continues the held card: the same engraved lines
        // and title, in place before its first zoomed frame.
        if (sourceDescription.layout || sourceDescription.title) CardDescEngraving.adopt(replacement, sourceDescription);
        /* Text the held card's engraving does not spell (a patched
           description, a renamed card: adopt refuses it) is engraved from the
           replacement's OWN resting lines, here, before the zoom below. Left
           natural it re-wrapped at 3.8x, and its first fit, scheduled when it
           was built, ran zoomed: it chose its size step there, and the kept
           hand node wore that size at rest after the hover. Engraving runs the
           first fits at rest (review of the merge, 28 September 2026).
           Contract: tests/unit/ui/handHoverChangedTextEngravesAtRest.test.js. */
        if (!CardDescEngraving.isEngraved(replacement) || !CardDescEngraving.isTitleEngraved(replacement)) {
            CardDescEngraving.engrave(replacement);
        }
        replacement.style.zoom = sourceZoom;
        replacement.style.margin = sourceMargin;
        replacement.style.transformOrigin = sourceTransformOrigin;
        replacement._hoverActivatedAt = activatedAt;
        if (sourcePosition) cardPositions.set(replacement, sourcePosition);

        activeCard = replacement;
        activeCardVisualRect = measureConnectedVisualRect(replacement) || activeCardVisualRect;
        overlayLockedToNone = !shouldOverlayCaptureForCard(replacement);
        applyOverlayPointerEvents('none', 'authoritative hand replacement');
        setStellarHoverState(replacement, true);
        Cards?.updateCardHint?.(replacement);
        return true;
    }

    function cancelCardHoverWork(card) {
        if (!card) return;
        card._hoverRasterGeneration = (Number(card._hoverRasterGeneration) || 0) + 1;
        card._hoverRasterPending = false;
        if (card._hoverTimer) {
            clearTimeout(card._hoverTimer);
            card._hoverTimer = undefined;
        }
        card._hoverDueAt = undefined;
        if (pendingCard === card) {
            pendingCard = null;
        }
    }

    // Function to update stored card positions
    function updateCardPositions(delayMs = 50) {
        const normalizedDelay = Number.isFinite(delayMs)
            ? Math.max(0, delayMs)
            : 50;
        if (isUpdatingPositions) {
            queuedPositionRefreshDelay = queuedPositionRefreshDelay === null
                ? normalizedDelay
                : Math.min(queuedPositionRefreshDelay, normalizedDelay);
            return;
        }
        const requestedDueAt = Date.now() + normalizedDelay;
        if (positionRefreshTimer) {
            if (requestedDueAt >= positionRefreshDueAt) return;
            clearTimeout(positionRefreshTimer);
        }
        positionRefreshDueAt = requestedDueAt;

        // Use setTimeout to ensure we're not in the middle of a DOM update
        positionRefreshTimer = setTimeout(() => {
            positionRefreshTimer = null;
            positionRefreshDueAt = 0;
            isUpdatingPositions = true;
            const cards = Array.from(document.querySelectorAll('#player-hand .card'));
            const liveCards = new Set(cards);
            let replacedHover = null;
            const detachedActiveCard = activeCard && !liveCards.has(activeCard)
                ? activeCard
                : null;
            const detachedPendingCard = pendingCard && !liveCards.has(pendingCard)
                ? pendingCard
                : null;

            if (detachedActiveCard?.dataset?.instanceId && activeCardVisualRect) {
                replacedHover = {
                    activationDelayMs: 0,
                    instanceId: detachedActiveCard.dataset.instanceId,
                    source: 'active',
                    // Detached elements report an empty viewport rect. Preserve
                    // only geometry measured while the enlarged card was still
                    // painted so a stationary pointer can follow the same
                    // authoritative instance onto its replacement node.
                    visualRect: activeCardVisualRect
                };
            } else if (detachedPendingCard?.dataset?.instanceId) {
                const dueAt = Number(detachedPendingCard._hoverDueAt);
                replacedHover = {
                    activationDelayMs: Number.isFinite(dueAt)
                        ? Math.max(0, dueAt - Date.now())
                        : 0,
                    instanceId: detachedPendingCard.dataset.instanceId,
                    source: 'pending',
                    visualRect: null
                };
            }

            // Hand renders replace every card node. Cancel delayed work and
            // release old nodes before rebuilding the position cache so a
            // detached card can neither enlarge nor stay strongly retained.
            for (const cachedCard of cardPositions.keys()) {
                if (!liveCards.has(cachedCard)) {
                    cancelCardHoverWork(cachedCard);
                }
            }
            if (detachedPendingCard) {
                cancelCardHoverWork(detachedPendingCard);
            }
            cardPositions.clear();
            if (detachedActiveCard) {
                cancelCardHoverWork(detachedActiveCard);
                resetHoverCard(detachedActiveCard);
                activeCard = null;
                activeCardVisualRect = null;
                overlayLockedToNone = false;
                overlay.style.pointerEvents = 'none';
            }

            cards.forEach(card => {
                // Skip cards that are being dragged
                if (card.classList.contains('dragging')) return;

                measureCardPosition(card);
            });
            anchorCardPositionsToHandRail();
            isUpdatingPositions = false;
            if (queuedPositionRefreshDelay !== null) {
                const queuedDelay = queuedPositionRefreshDelay;
                queuedPositionRefreshDelay = null;
                updateCardPositions(queuedDelay);
            }

            // Authoritative enemy snapshots rebuild even an unchanged player
            // hand. If the same card instance is still under a stationary
            // pointer, reacquire it immediately through the normal hover
            // pipeline instead of waiting for a mousemove that may never come.
            const replacement = replacedHover
                ? cards.find((card) => (
                    card.dataset?.instanceId === replacedHover.instanceId
                ))
                : null;
            if (replacement) {
                const scheduledPointerRevision = pointerRevision;
                setTimeout(() => {
                    if (pointerRevision !== scheduledPointerRevision) return;
                    const pointer = readLastHandPointer();
                    if (!Number.isFinite(pointer.x) || !Number.isFinite(pointer.y)) return;
                    const supportsPaintHitTest = typeof document.elementsFromPoint === 'function';
                    const paintedReplacement = supportsPaintHitTest
                        ? paintedHandCardAtPoint(pointer)
                        : null;
                    if (pointerRevision !== scheduledPointerRevision) return;
                    const hasActiveVisualContinuity = replacedHover.source === 'active'
                        && isWithinRetentionArea(pointer, replacedHover.visualRect, {
                            sidePadding: 0,
                            topExpansion: 0,
                            bottomPadding: 0
                        });
                    if (
                        supportsPaintHitTest
                        && paintedReplacement !== replacement
                        && !hasActiveVisualContinuity
                    ) {
                        return;
                    }
                    handleHandMouseMove?.({
                        clientX: pointer.x,
                        clientY: pointer.y,
                        isTrusted: false,
                        reacquireActivationDelayMs: replacedHover.activationDelayMs,
                        reacquireInstanceId: replacedHover.instanceId,
                        reacquirePointerRevision: scheduledPointerRevision,
                        reacquireVisualContinuity: hasActiveVisualContinuity
                    });
                }, 0);
            }
        }, normalizedDelay);
    }

    /* A SCROLL MOVES EVERY CACHED RECTANGLE AND NOTHING ELSE TELLS US.
       Hover is decided against `cardPositions`, a cache refreshed on resize and
       on DOM mutation. A scroll changes every card's viewport rectangle without
       firing either, so afterwards the pointer is compared against where the
       cards USED to be and hovering a hand card silently does nothing — no
       enlargement, no tilt, no hint. Measured: a probe that scrolled before
       hovering read a growth factor of 1.00 on a card that grows 3.8x without
       the scroll.

       The cache is SHIFTED rather than re-measured, using the hand rail's own
       movement as the delta. Re-measuring would read the currently enlarged
       card's grown box as its resting slot and break hover a different way, and
       the rail's delta is correct for any scrolling ancestor, the visual
       viewport included. */
    let handScrollAnchor = null;

    function handRailOrigin() {
        const hand = document.getElementById('player-hand');
        if (!hand || typeof hand.getBoundingClientRect !== 'function') return null;
        const rect = hand.getBoundingClientRect();
        // A hand that is not being rendered (the lobby, a finished match) reports
        // an all-zero box. Treating that as a position would hand the next
        // scroll a delta the size of the whole rail and move every cached
        // rectangle to nowhere.
        if (!rect.width && !rect.height) return null;
        return { left: rect.left, top: rect.top };
    }

    function anchorCardPositionsToHandRail() {
        handScrollAnchor = handRailOrigin();
    }

    /**
     * Did THIS scroll move the hand?
     *
     * The listener is bound in the capture phase, so it sees every scroller in
     * the document — a catalogue grid, the story feed, a modal. Only a scroll of
     * something that CONTAINS the hand moves the hand's cards, and shifting the
     * cache on an unrelated one would apply a delta the rail did not travel.
     */
    function scrollMovesTheHand(event) {
        const target = event?.target;
        if (!target || target === document || target === window) return true;
        const hand = document.getElementById('player-hand');
        if (!hand) return false;
        return typeof target.contains === 'function' && target.contains(hand);
    }

    function realignCardPositionsToScroll(event) {
        /* Cheapest possible early exit FIRST. This runs in the capture phase for
           every scroll anywhere in the document — including the collection grid
           in the lobby, where there is no hand at all — and `getBoundingClientRect`
           inside a scroll handler forces layout. With no cached positions there
           is nothing to realign, so the common case costs one Map size read. */
        if (!cardPositions.size) return;
        if (event && !scrollMovesTheHand(event)) return;
        const origin = handRailOrigin();
        if (!origin) return;
        if (!handScrollAnchor) {
            handScrollAnchor = origin;
            return;
        }
        const deltaX = origin.left - handScrollAnchor.left;
        const deltaY = origin.top - handScrollAnchor.top;
        handScrollAnchor = origin;
        if (!deltaX && !deltaY) return;
        for (const position of cardPositions.values()) {
            position.left += deltaX;
            position.right += deltaX;
            position.top += deltaY;
            position.bottom += deltaY;
        }
        if (activeCardVisualRect) {
            activeCardVisualRect.left += deltaX;
            activeCardVisualRect.right += deltaX;
            activeCardVisualRect.top += deltaY;
            activeCardVisualRect.bottom += deltaY;
        }
    }

    // Update positions initially and whenever the window resizes
    updateCardPositions();
    anchorCardPositionsToHandRail();
    window.addEventListener('resize', () => updateCardPositions());
    // The hand glides its cards into new seats after a rebuild (cards.js); the
    // boxes cached while they moved are re-measured once they have landed.
    window.addEventListener('moonfall:hand-reflowed', () => updateCardPositions(0));
    /* Bound on WINDOW in the capture phase, which is the only placement that
       sees every scroll: `scroll` does not bubble, so an ordinary listener
       misses a scrolling ancestor of the hand, and a capturing listener on
       `document` misses the events whose target IS the window. Capture starts
       at the window, so this one sees all three. */
    window.addEventListener('scroll', realignCardPositionsToScroll, {
        capture: true,
        passive: true,
    });
    // The visual viewport moves on pinch-zoom and when a mobile URL bar
    // collapses — both change every card's viewport rectangle with no page
    // scroll and no mutation.
    window.visualViewport?.addEventListener?.('scroll', realignCardPositionsToScroll,
        { passive: true });
    window.visualViewport?.addEventListener?.('resize', realignCardPositionsToScroll,
        { passive: true });

    // Also update positions when cards might have changed (new game, etc.)
    const gameObserver = new MutationObserver(() => {
        const trackedHoverWasReplaced = (
            (activeCard && !activeCard.isConnected)
            || (pendingCard && !pendingCard.isConnected)
        );
        if (trackedHoverWasReplaced && handoffActiveCardToReplacement()) {
            clearTimeout(window.cardPositionUpdateTimer);
            window.cardPositionUpdateTimer = setTimeout(updateCardPositions, 100);
            return;
        }
        if (trackedHoverWasReplaced) {
            clearTimeout(window.cardPositionUpdateTimer);
            updateCardPositions(0);
            return;
        }
        // Debounce the updates to prevent excessive recalculations
        clearTimeout(window.cardPositionUpdateTimer);
        window.cardPositionUpdateTimer = setTimeout(updateCardPositions, 100);
    });

    const gameView = document.getElementById('game-view');
    if (gameView) {
        gameObserver.observe(gameView, {
            childList: true,
            subtree: true
        });
    }

    // Create a transparent overlay to capture mouse events
    const overlay = document.createElement('div');
    overlay.id = 'hover-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.pointerEvents = 'none'; // Initially doesn't capture events
    /* 5000 — above the resting hand rail (4500) and the two transient board FX
       tiers, below #fx-layer (9400). The comment here used to say "higher than
       any enlarged card (hover-active z-index is 1000)", which has been wrong
       since those rules moved to 9000/9500; it does not matter, because this
       layer is transparent and `shouldOverlayCaptureForCard` forces
       `pointer-events: none` for every #player-hand card, so it neither paints
       nor captures while a hand card is enlarged. That is also why the rail may
       rise past it to 5200 (game-board.css) without changing hover behaviour. */
    overlay.style.zIndex = '5000';
    document.body.appendChild(overlay);
    hoverLog('Overlay created', { pointerEvents: overlay.style.pointerEvents });

    // Helper: decide if overlay should intercept events for a given active card
    const shouldOverlayCaptureForCard = (card) => {
        if (!card) return true;
        const zone = card.dataset?.zone || '';
        if (zone === 'player-hand' || card.closest?.('#player-hand')) {
            // Keep pointer events on the card so tilt can continue updating while enlarged.
            return false;
        }
        const isExplicitlyDraggable = card.getAttribute('draggable') === 'true' || card.classList.contains('draggable');
        const isTargetableSpell = card.dataset && card.dataset.type === 'Spell' && card.dataset.targetingType === 'Targetable';
        return !(isExplicitlyDraggable || isTargetableSpell);
    };

    const applyOverlayPointerEvents = (desiredValue, reason) => {
        const value = overlayLockedToNone ? 'none' : desiredValue;
        overlay.style.pointerEvents = value;
        hoverLog(`Overlay pointerEvents -> ${value}`, {
            reason,
            desired: desiredValue,
            locked: overlayLockedToNone,
            activeCardId: activeCard?.dataset?.instanceId || null
        });
    };

    const cancelPendingHandHovers = (cards = document.querySelectorAll('#player-hand .card')) => {
        cards.forEach(card => {
            // Invalidate a soft raster gate as well as its timer. Custom drag
            // suppresses compatibility mouse/drag events, so a pending hover
            // promise would otherwise be free to reactivate this card after
            // another surface has taken ownership of the gesture.
            cancelCardHoverWork(card);
        });
    };

    const clearActiveHandHover = (reason = 'external focus owner') => {
        cancelPendingHandHovers();
        if (activeCard) {
            resetHoverCard(activeCard);
            activeCard = null;
            activeCardVisualRect = null;
        }
        overlayLockedToNone = false;
        applyOverlayPointerEvents('none', reason);
    };
    CardHover.clearActiveHandHover = () => {
        clearActiveHandHover('external focus owner');
    };

    // --- Enlarged Card Press Handling ---
    // When a card is enlarged, the overlay becomes active to prevent hover-trapping.
    // A press must still reach the card underneath so a drag can begin — dragging
    // is the only way to play a card (owner, 2026-09-24) — so the overlay's
    // pointer-events are managed around each press.

    // 1. On 'mousedown', always disable the overlay first and forward the
    //    event to the element underneath so drag operations can begin even if
    //    the previously enlarged card is still active.
    overlay.addEventListener('mousedown', (e) => {
        hoverLog('Overlay mousedown', {
            isTrusted: e.isTrusted,
            pointerEvents: overlay.style.pointerEvents,
            activeCardId: activeCard?.dataset?.instanceId || null,
            target: e.target && (e.target.id || e.target.className)
        });

        // Allow events to reach underlying elements
        overlayLockedToNone = false; // this gesture explicitly disables overlay
        applyOverlayPointerEvents('none', 'overlay mousedown');

        // Forward the mousedown to the element now revealed beneath the cursor
        const elementUnder = document.elementFromPoint(e.clientX, e.clientY);
        if (elementUnder) {
            const forwardedEvent = new MouseEvent('mousedown', {
                bubbles: true,
                cancelable: true,
                clientX: e.clientX,
                clientY: e.clientY,
                button: e.button,
                buttons: e.buttons,
                ctrlKey: e.ctrlKey,
                shiftKey: e.shiftKey,
                altKey: e.altKey,
                metaKey: e.metaKey
            });
            elementUnder.dispatchEvent(forwardedEvent);
            hoverLog('Forwarded mousedown to underlying element', {
                underlyingTag: elementUnder.tagName,
                underlyingId: elementUnder.id,
                underlyingClasses: elementUnder.className
            });
        }
    });

    /* (A mouseup that had not moved used to be turned into a synthetic click on
       the card underneath, which is how an enlarged card was clicked to summon
       or cast. No click plays a card any more, so nothing is forwarded.) */

    // 2. After a click is fully processed, a capture-phase listener re-enables the overlay.
    document.addEventListener('click', () => {
        if (activeCard && overlay.style.pointerEvents === 'none' && !dragInProgress) {
            applyOverlayPointerEvents('auto', 'click completes');
        }
    }, true);

    // Extra safeguard: if the user starts interacting (mousedown) on a non-enlarged card,
    // immediately disable the overlay so the event sequence (mousedown → mousemove) reaches
    // the card in time to initiate dragging.
    document.addEventListener('mousedown', (e) => {
        const cardTarget = e.target && e.target.closest('.card');
        if (cardTarget && !cardTarget.classList.contains('hover-active')) {
            // Disable the overlay so the mousedown/mousemove sequence reaches the card,
            // allowing the native drag gesture to initiate.
            overlayLockedToNone = !shouldOverlayCaptureForCard(cardTarget);
            applyOverlayPointerEvents('none', 'mousedown on non-enlarged card');

            // If another card is currently enlarged, deactivate it first.
            if (activeCard) {
                resetHoverCard(activeCard);
                activeCard = null;
                activeCardVisualRect = null;
            }

            // --- NEW: Cancel any pending hover timers so this card (or any other)
            // does not auto-enlarge while the user is starting a drag. Auto-enlarging
            // mid-drag was preventing the browsers from firing the dragstart event
            // consistently on smaller, “unenlarged” cards, especially on high-DPI
            // laptops.
            cancelPendingHandHovers();
        }
    });

    // 3. For explicit drag operations, also disable the overlay to ensure the card's
    //    drag handlers fire correctly.
    document.addEventListener('dragstart', (e) => {
        // Always disable the overlay so underlying elements receive drag events.
        overlayLockedToNone = true;
        applyOverlayPointerEvents('none', 'global dragstart');
        dragInProgress = true;
        const draggedCard = e.target && e.target.closest('.card');
        hoverLog('Global dragstart', {
            isTrusted: e.isTrusted,
            draggedCardId: draggedCard?.dataset?.instanceId || draggedCard?.id || null,
            overlayPointerEvents: overlay.style.pointerEvents,
            activeCardId: activeCard?.dataset?.instanceId || null
        });

        // If we start dragging a different card than the currently enlarged one,
        // clear the hover-active state so dragend won’t re-enable the overlay.
        // Clear any pending hover timers to prevent overlay from re-enabling mid-drag
        cancelPendingHandHovers();

        if (activeCard && draggedCard !== activeCard) {
            resetHoverCard(activeCard);
            activeCard = null;
            activeCardVisualRect = null;
        }
    });
    document.addEventListener('dragend', () => {
        dragInProgress = false;
        hoverLog('Global dragend', {
            activeCardId: activeCard?.dataset?.instanceId || null,
            overlayPointerEvents: overlay.style.pointerEvents
        });
        // Re-enable the overlay only if a card is still enlarged.
        if (activeCard) {
            overlayLockedToNone = !shouldOverlayCaptureForCard(activeCard);
            applyOverlayPointerEvents('auto', 'dragend with active card');
        }
    });

    // Handle mousemove events to determine if mouse is over original card position
    handleHandMouseMove = (e) => {
        const cards = document.querySelectorAll('#player-hand .card');
        const storyFocusBackdrop = document.querySelector(
            '#story-feed-focus-backdrop:not([hidden])'
        );
        if (storyFocusBackdrop) {
            clearActiveHandHover('story reveal owns focus');
            return;
        }
        // TouchPreview owns tap inspection and its compatibility-mouse window:
        // ignore trusted moves while that touch interaction is settling.
        if (e?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
        let foundHoveredCard = false;
        const pointer = { x: e.clientX, y: e.clientY };
        const isReplacementReacquire = Boolean(e?.reacquireInstanceId);
        if (!isReplacementReacquire) {
            pointerRevision += 1;
            window.lastMouseX = e.clientX;
            window.lastMouseY = e.clientY;
        }
        const supportsPaintHitTest = typeof document.elementsFromPoint === 'function';
        const paintedCard = paintedHandCardAtPoint(pointer);

        // If a drag is active, suppress all hover enlargements
        const dragActive = dragInProgress ||
            document.body.classList.contains('card-dragging') ||
            (CardDrag?.isDragging?.()) ||
            ((typeof GameState !== 'undefined' && typeof GameState.get === 'function') && GameState.get().draggedCard);

        if (dragActive) {
            cancelPendingHandHovers(cards);
            if (activeCard) {
                resetHoverCard(activeCard);
                activeCard = null;
                activeCardVisualRect = null;
            }
            applyOverlayPointerEvents('none', 'drag active - suppress hover');
            return;
        }

        cards.forEach(card => {
            // Skip cards that are being dragged
            if (card.classList.contains('dragging')) return;

            // Hand renders can land between the user's pointer move and the
            // debounced MutationObserver refresh. Measure an uncached live card
            // synchronously so the first deliberate hover is never dropped.
            const pos = cardPositions.get(card) || measureCardPosition(card);
            if (!pos) return;

            const withinOriginalSlot = pointer.x >= pos.left && pointer.x <= pos.right &&
                pointer.y >= pos.top && pointer.y <= pos.bottom;
            const handRetentionOptions = getHandHoverRetentionOptions(pos);
            const withinActiveVisual = activeCard === card &&
                isWithinActiveCardHoverTarget(pointer, card, {
                    // In hand, keep hover based on the rendered enlarged card so
                    // 3D rotation remains stable for both playable and unplayable cards,
                    // even when transient overlays are on top of the card.
                    constrainToRetentionArea: false,
                    useVisualBoundsFallback: true,
                    preferVisualBounds: true,
                    visualBoundsOptions: {
                        sidePadding: 16,
                        topPadding: 24,
                        bottomPadding: 14
                    }
                });
            const hasReplacementVisualContinuity = Boolean(
                e.reacquireVisualContinuity
                && card.dataset?.instanceId === e.reacquireInstanceId
                && pointerRevision === e.reacquirePointerRevision
            );

            // For the active enlarged card, rely on rendered visual bounds instead of cached
            // slot bounds, which may become stale during board updates.
            const withinHoverTarget = activeCard === card
                ? withinActiveVisual
                : (withinOriginalSlot || hasReplacementVisualContinuity);

            if (withinHoverTarget) {
                // Rotated fans have overlapping axis-aligned rectangles. The
                // browser's paint-order hit test, not DOM iteration order,
                // decides which resting card visually owns this point.
                if (
                    activeCard !== card
                    && supportsPaintHitTest
                    && paintedCard !== card
                    && !hasReplacementVisualContinuity
                ) {
                    return;
                }
                if (activeCard && card !== activeCard &&
                    !isWithinSwitchTargetCore(
                        pointer,
                        pos
                    )) {
                    return;
                }
                // Mouse is over the original card position
                foundHoveredCard = true;

                // Check if we're already hovering this card or have a pending hover
                if (
                    activeCard === card
                    || card._hoverTimer !== undefined
                    || card._hoverRasterPending === true
                    || pendingCard === card
                ) {
                if (activeCard === card) {
                    applyTiltFromPointer(card, pointer, getHandTiltPointerOptions(card));
                    activeCardVisualRect = measureConnectedVisualRect(card) || activeCardVisualRect;
                }
                    return;
                }

                // Deactivate previous card and clear any pending timers
                if (activeCard) {
                    resetHoverCard(activeCard);
                    overlayLockedToNone = false;
                    applyOverlayPointerEvents('none', 'deactivated previous active card');
                    activeCard = null;
                    activeCardVisualRect = null;
                }

                // Clear any existing hover timers on all cards
                cancelPendingHandHovers(cards);

                // Set a timer to activate this card after a short delay
                const activationDelay = Number.isFinite(e.reacquireActivationDelayMs)
                    ? Math.max(0, e.reacquireActivationDelayMs)
                    : (
                    e.reacquireInstanceId
                    && card.dataset?.instanceId === e.reacquireInstanceId
                    ) ? 0 : 120;
                pendingCard = card;
                card._hoverDueAt = Date.now() + activationDelay;
                card._hoverTimer = setTimeout(() => {
                    card._hoverTimer = undefined;
                    card._hoverDueAt = undefined;
                    if (pendingCard === card) {
                        pendingCard = null;
                    }
                    const activationGeneration = (Number(card._hoverRasterGeneration) || 0) + 1;
                    card._hoverRasterGeneration = activationGeneration;

                    const readLivePointer = () => {
                        const latestX = Number(window.lastMouseX);
                        const latestY = Number(window.lastMouseY);
                        return {
                            x: Number.isFinite(latestX) ? latestX : pointer.x,
                            y: Number.isFinite(latestY) ? latestY : pointer.y
                        };
                    };
                    const canActivate = () => {
                        const livePointer = readLivePointer();
                        const currentPos = cardPositions.get(card);
                        const currentHand = document.getElementById('player-hand');
                        const ownsPaintedPoint = (
                            typeof document.elementsFromPoint !== 'function'
                            || paintedHandCardAtPoint(livePointer) === card
                        );
                        const currentVisualContinuity = hasReplacementVisualContinuity
                            && pointerRevision === e.reacquirePointerRevision;
                        const withinCurrentSlot = currentPos
                            && livePointer.x >= currentPos.left
                            && livePointer.x <= currentPos.right
                            && livePointer.y >= currentPos.top
                            && livePointer.y <= currentPos.bottom;
                        return card._hoverRasterGeneration === activationGeneration
                            && card.isConnected
                            && currentHand?.contains(card)
                            && currentPos
                            && (
                                (withinCurrentSlot && ownsPaintedPoint)
                                || currentVisualContinuity
                            );
                    };
                    const activate = () => {
                        if (!canActivate()) return;
                        if (pendingCard === card) {
                            pendingCard = null;
                        }
                        card._hoverRasterPending = false;
                        const livePointer = readLivePointer();
                        card.classList.add('hover-active');
                        applyDynamicHoverTransform(card);
                        setStellarHoverState(card, true);
                        applyTiltFromPointer(card, livePointer, getHandTiltPointerOptions(card));
                        card._hoverActivatedAt = Date.now();
                        activeCard = card;
                        activeCardVisualRect = measureConnectedVisualRect(card);
                        overlayLockedToNone = !shouldOverlayCaptureForCard(card);
                        hoverLog('Hover activated card', {
                            cardId: card.dataset?.instanceId || card.id || null,
                            pointerEvents: overlay.style.pointerEvents,
                            overlayLockedToNone
                        });
                        // Update hover hint for the newly active card
                        if (Cards && typeof Cards.updateCardHint === 'function') {
                            Cards.updateCardHint(card);
                        }

                        // Enable the overlay after animation completes, but NEVER during a drag
                        setTimeout(() => {
                            if (activeCard === card) {
                                const desired = dragInProgress || overlayLockedToNone ? 'none' : 'auto';
                                applyOverlayPointerEvents(desired, 'hover activation settle');
                            }
                        }, 150);
                    };

                    const lock = Cards?.lockCardRaster;
                    const ready = typeof lock !== 'function'
                        || lock(card, 'hover') !== false;
                    if (ready) {
                        activate();
                        return;
                    }
                    const prepare = Cards?.prepareCardRasterForInteraction;
                    if (typeof prepare !== 'function') return;
                    pendingCard = card;
                    card._hoverRasterPending = true;
                    void Promise.resolve(prepare(card, 'hover')).then((settled) => {
                        if (card._hoverRasterGeneration !== activationGeneration) return;
                        if (pendingCard === card) {
                            pendingCard = null;
                        }
                        card._hoverRasterPending = false;
                        if (settled) activate();
                    });
                }, activationDelay);
            }
        });

        // If mouse is not over any original card position
        if (!foundHoveredCard) {
            // Retain hover only while pointer remains over rendered card bounds.
            // Avoid timed grace retention here so cards reliably unselect on exit.
            if (activeCard) {
                const activePos = cardPositions.get(activeCard);
                const handRetentionOptions = getHandHoverRetentionOptions(activePos);
                const activeCardHasPointerEventsDisabled = hasPointerEventsDisabled(activeCard);
                const retainActiveHover = shouldRetainActiveHover(pointer, activeCard, activePos, {
                    activatedAt: activeCard._hoverActivatedAt,
                    // Keep normal cards responsive to unhover; only use grace on pointer-disabled cards.
                    graceMs: activeCardHasPointerEventsDisabled ? 90 : 0,
                    useVisualBoundsFallback: true,
                    preferVisualBounds: true,
                    constrainElementHitToRetentionArea: false,
                    visualBoundsOptions: {
                        sidePadding: 16,
                        topPadding: 24,
                        bottomPadding: 14
                    },
                    retentionAreaOptions: handRetentionOptions
                });
                if (retainActiveHover) {
                    applyTiltFromPointer(activeCard, { x: e.clientX, y: e.clientY }, getHandTiltPointerOptions(activeCard));
                    activeCardVisualRect = measureConnectedVisualRect(activeCard) || activeCardVisualRect;
                    return;
                }
            }

            // Clear any pending hover timers
            cancelPendingHandHovers(cards);

            // Deactivate any active card
            if (activeCard) {
                resetHoverCard(activeCard);
                activeCard = null;
                activeCardVisualRect = null;
                overlayLockedToNone = false;
                overlay.style.pointerEvents = 'none';
                hoverLog('Cleared active card on mousemove', {
                    pointerEvents: overlay.style.pointerEvents
                });
            }
        }

    };
    document.addEventListener('mousemove', handleHandMouseMove);
}

export { CardHover };
